import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useActivity } from '../App';
import toast from 'react-hot-toast';

const SettingSection = ({ title, desc, children }) => (
  <div className="card" style={{ marginBottom: '20px' }}>
    <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid var(--border-color)' }}>
      <h3 style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--text-primary)' }}>{title}</h3>
      {desc && <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: '4px' }}>{desc}</p>}
    </div>
    {children}
  </div>
);

const ToggleRow = ({ label, desc, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
    <div>
      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>{label}</div>
      {desc && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>{desc}</div>}
    </div>
    <label className="toggle-switch">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="toggle-slider" />
    </label>
  </div>
);

const Settings = () => {
  const { user } = useAuth();
  const { addLog } = useActivity();
  const [activeTab, setActiveTab] = useState('general');
  const [saved, setSaved]         = useState(false);

  const [general, setGeneral] = useState({
    appName: 'WalkBattle Admin',
    appUrl: 'https://walkbattle.in',
    timezone: 'Asia/Kolkata',
    dateFormat: 'DD/MM/YYYY',
    language: 'en',
    currency: 'INR',
    maintenanceMode: false,
  });

  const [notifications, setNotifs] = useState({
    emailNewUser:      true,
    emailReports:      true,
    emailSecurity:     true,
    pushEnabled:       true,
    pushMarketing:     false,
    slackIntegration:  false,
    slackWebhook:      '',
    digestFrequency:   'daily',
  });

  const [security, setSecurity] = useState({
    twoFactor:         false,
    sessionTimeout:    '30',
    loginAttempts:     '5',
    passwordMinLength: '8',
    requireSpecialChar:true,
    requireNumbers:    true,
    ipWhitelist:       '',
    auditLogs:         true,
  });

  const [email, setEmail] = useState({
    smtpHost:   'smtp.gmail.com',
    smtpPort:   '587',
    smtpUser:   'noreply@walkbattle.in',
    smtpPass:   '',
    fromName:   'WalkBattleAdmin',
    fromEmail:  'noreply@walkbattle.in',
    testEmail:  '',
  });

  const handleSave = () => {
    addLog('Settings Updated', activeTab + ' settings', 'update');
    setSaved(true);
    toast.success('Settings saved!');
    setTimeout(() => setSaved(false), 2500);
  };

  const TABS = [
    { id: 'general',       label: '⚙️ General' },
    { id: 'notifications', label: '🔔 Notifications' },
    { id: 'security',      label: '🔒 Security' },
    { id: 'email',         label: '📧 Email (SMTP)' },
    { id: 'appearance',    label: '🎨 Appearance' },
  ];

  return (
    <div className="animate-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="breadcrumb"><span>System</span> / <span className="current">Settings</span></div>
          <h1>Application Settings</h1>
          <p>Configure your application preferences</p>
        </div>
        <button className="btn btn-primary" onClick={handleSave}>
          {saved ? '✓ Saved!' : 'Save Changes'}
        </button>
      </div>

      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Sidebar Tabs */}
        <div className="card" style={{ width: '200px', flexShrink: 0, padding: '8px' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                width: '100%', padding: '10px 14px', textAlign: 'left',
                background: activeTab === tab.id ? 'rgba(99,102,241,0.15)' : 'transparent',
                border: 'none', borderRadius: '8px', cursor: 'pointer',
                color: activeTab === tab.id ? 'var(--accent-secondary)' : 'var(--text-secondary)',
                fontSize: '0.85rem', fontWeight: activeTab === tab.id ? 600 : 400,
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: '2px', transition: 'all 0.15s',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          {/* General */}
          {activeTab === 'general' && (
            <SettingSection title="General Settings" desc="Basic application configuration">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Application Name</label>
                  <input className="form-control" value={general.appName}
                    onChange={e => setGeneral(p=>({...p, appName: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Application URL</label>
                  <input className="form-control" value={general.appUrl}
                    onChange={e => setGeneral(p=>({...p, appUrl: e.target.value}))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Timezone</label>
                  <select className="form-control" value={general.timezone}
                    onChange={e => setGeneral(p=>({...p, timezone: e.target.value}))}>
                    {['Asia/Kolkata','Asia/Dubai','America/New_York','Europe/London','UTC'].map(tz => (
                      <option key={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date Format</label>
                  <select className="form-control" value={general.dateFormat}
                    onChange={e => setGeneral(p=>({...p, dateFormat: e.target.value}))}>
                    {['DD/MM/YYYY','MM/DD/YYYY','YYYY-MM-DD'].map(f => <option key={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Language</label>
                  <select className="form-control" value={general.language}
                    onChange={e => setGeneral(p=>({...p, language: e.target.value}))}>
                    <option value="en">English</option>
                    <option value="hi">Hindi</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Default Currency</label>
                  <select className="form-control" value={general.currency}
                    onChange={e => setGeneral(p=>({...p, currency: e.target.value}))}>
                    {['INR','USD','EUR','GBP'].map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <ToggleRow
                label="Maintenance Mode"
                desc="Disable public access while you make updates"
                checked={general.maintenanceMode}
                onChange={e => setGeneral(p=>({...p, maintenanceMode: e.target.checked}))}
              />
            </SettingSection>
          )}

          {/* Notifications */}
          {activeTab === 'notifications' && (
            <SettingSection title="Notification Settings" desc="Control how you receive notifications">
              <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>Email Notifications</h4>
              <ToggleRow label="New User Registration" desc="Get notified when a new user signs up" checked={notifications.emailNewUser} onChange={e => setNotifs(p=>({...p, emailNewUser: e.target.checked}))} />
              <ToggleRow label="Weekly Reports"        desc="Receive weekly analytics reports"       checked={notifications.emailReports}  onChange={e => setNotifs(p=>({...p, emailReports: e.target.checked}))} />
              <ToggleRow label="Security Alerts"       desc="Login attempts and security events"     checked={notifications.emailSecurity} onChange={e => setNotifs(p=>({...p, emailSecurity: e.target.checked}))} />
              <div style={{ marginTop: '16px', marginBottom: '8px' }}>
                <h4 style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Push Notifications</h4>
              </div>
              <ToggleRow label="Enable Push" checked={notifications.pushEnabled} onChange={e => setNotifs(p=>({...p, pushEnabled: e.target.checked}))} />
              <ToggleRow label="Marketing Alerts" desc="Promotional and campaign notifications" checked={notifications.pushMarketing} onChange={e => setNotifs(p=>({...p, pushMarketing: e.target.checked}))} />
              <div style={{ marginTop: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Digest Frequency</label>
                  <select className="form-control" style={{ maxWidth: '200px' }} value={notifications.digestFrequency}
                    onChange={e => setNotifs(p=>({...p, digestFrequency: e.target.value}))}>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </div>
            </SettingSection>
          )}

          {/* Security */}
          {activeTab === 'security' && (
            <SettingSection title="Security Settings" desc="Protect your admin panel">
              <ToggleRow label="Two-Factor Authentication" desc="Require 2FA for all admin logins" checked={security.twoFactor} onChange={e => setSecurity(p=>({...p, twoFactor: e.target.checked}))} />
              <ToggleRow label="Audit Logs"               desc="Track all admin actions"          checked={security.auditLogs} onChange={e => setSecurity(p=>({...p, auditLogs: e.target.checked}))} />
              <div className="form-row" style={{ marginTop: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Session Timeout (minutes)</label>
                  <input className="form-control" type="number" value={security.sessionTimeout}
                    onChange={e => setSecurity(p=>({...p, sessionTimeout: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Max Login Attempts</label>
                  <input className="form-control" type="number" value={security.loginAttempts}
                    onChange={e => setSecurity(p=>({...p, loginAttempts: e.target.value}))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Min Password Length</label>
                  <input className="form-control" type="number" value={security.passwordMinLength}
                    onChange={e => setSecurity(p=>({...p, passwordMinLength: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">IP Whitelist (comma-separated)</label>
                  <input className="form-control" value={security.ipWhitelist} placeholder="e.g. 192.168.1.1, 10.0.0.1"
                    onChange={e => setSecurity(p=>({...p, ipWhitelist: e.target.value}))} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={security.requireSpecialChar}
                    onChange={e => setSecurity(p=>({...p, requireSpecialChar: e.target.checked}))} />
                  Require special characters
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={security.requireNumbers}
                    onChange={e => setSecurity(p=>({...p, requireNumbers: e.target.checked}))} />
                  Require numbers
                </label>
              </div>
            </SettingSection>
          )}

          {/* Email SMTP */}
          {activeTab === 'email' && (
            <SettingSection title="Email (SMTP) Configuration" desc="Configure your email sending service">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SMTP Host</label>
                  <input className="form-control" value={email.smtpHost} onChange={e => setEmail(p=>({...p, smtpHost: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">SMTP Port</label>
                  <input className="form-control" type="number" value={email.smtpPort} onChange={e => setEmail(p=>({...p, smtpPort: e.target.value}))} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">SMTP Username</label>
                  <input className="form-control" value={email.smtpUser} onChange={e => setEmail(p=>({...p, smtpUser: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">SMTP Password</label>
                  <input className="form-control" type="password" value={email.smtpPass} onChange={e => setEmail(p=>({...p, smtpPass: e.target.value}))} placeholder="App password" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">From Name</label>
                  <input className="form-control" value={email.fromName} onChange={e => setEmail(p=>({...p, fromName: e.target.value}))} />
                </div>
                <div className="form-group">
                  <label className="form-label">From Email</label>
                  <input className="form-control" value={email.fromEmail} onChange={e => setEmail(p=>({...p, fromEmail: e.target.value}))} />
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px' }}>
                <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label className="form-label">Send Test Email</label>
                  <input className="form-control" type="email" value={email.testEmail} onChange={e => setEmail(p=>({...p, testEmail: e.target.value}))} placeholder="test@example.com" />
                </div>
                <button className="btn btn-secondary">Send Test</button>
              </div>
            </SettingSection>
          )}

          {/* Appearance */}
          {activeTab === 'appearance' && (
            <SettingSection title="Appearance" desc="Customize the look and feel">
              <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                {[
                  { name: 'Dark (Default)', bg: '#0f172a', sidebar: '#1e293b', accent: '#6366f1' },
                  { name: 'Midnight',       bg: '#111827', sidebar: '#1f2937', accent: '#8b5cf6' },
                  { name: 'Ocean Dark',     bg: '#0a1628', sidebar: '#0f2744', accent: '#06b6d4' },
                ].map((theme, i) => (
                  <div key={i} style={{
                    border: `2px solid ${i === 0 ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    borderRadius: '12px', overflow: 'hidden', cursor: 'pointer', width: '160px',
                    transition: 'border-color 0.2s',
                  }}>
                    <div style={{ height: '80px', background: theme.bg, display: 'flex', padding: '8px', gap: '6px' }}>
                      <div style={{ width: '28px', background: theme.sidebar, borderRadius: '6px' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ height: '10px', background: theme.sidebar, borderRadius: '3px', marginBottom: '4px' }} />
                        <div style={{ height: '28px', background: theme.accent + '30', borderRadius: '4px' }} />
                      </div>
                    </div>
                    <div style={{ padding: '8px 10px', background: 'var(--bg-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{theme.name}</span>
                      {i === 0 && <span style={{ fontSize: '0.65rem', background: theme.accent + '20', color: theme.accent, padding: '2px 6px', borderRadius: '4px' }}>Active</span>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="form-group mt-4">
                <label className="form-label">Custom Accent Color</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="color" defaultValue="#6366f1" style={{ width: '48px', height: '36px', borderRadius: '8px', border: '1px solid var(--border-color)', background: 'transparent', cursor: 'pointer' }} />
                  <input className="form-control" defaultValue="#6366f1" style={{ maxWidth: '140px' }} />
                </div>
              </div>
            </SettingSection>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={handleSave} style={{ minWidth: '140px' }}>
              {saved
                ? <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Saved!</>
                : 'Save Changes'
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
